import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button, Form, Row, Col, Image, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';

import { setupTenantProfile, signOut } from '../../store/actions/users';
import { errorHelper, getAuthHeader } from '../../utils/tools';
import CompanySearch from '../companySearch';
import { PLAN_LIMITS, PPWR_PLAN_LIMITS, FREE_PLAN, priceWithVat, VAT_RATE } from '../../config/plans';
import { BsCheckLg } from 'react-icons/bs';
import { IoCubeOutline } from 'react-icons/io5';

import logo from '../../assets/logo.svg';
import '../../styles/tenantSetup.css';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
    : null;

const validation = Yup.object({
    companyName: Yup.string().required('Meno spoločnosti/obce/živnosti je povinné.'),
    address: Yup.object({
        street: Yup.string()
            .trim()
            .required('Ulica je povinná.')
            .min(5, 'Minimálny počet znakov je 5.')
            .max(50, 'Maximálny počet znakov je 50.'),
        registrationNumber: Yup.number()
            .typeError('Súpisné číslo musí byť číslo')
            .max(9999999, 'Neplatná hodnota.'),
        buildingNumber: Yup.string().max(25, 'Maximálny počet znakov je 25.'),
        zip: Yup.string()
            .required('PSČ je povinné.')
            .matches(/^\d*$/, 'PSČ nie je vo formáte slovenského PSČ.')
            .length(5, 'PSČ musí mať 5 číslic.'),
        city: Yup.string()
            .required('Mesto je povinné.')
            .min(2, 'Minimálny počet znakov je 2.')
            .max(50, 'Maximálny počet znakov je 50.'),
        country: Yup.string().required('Krajina je povinná.'),
    }),
    identificationNumber: Yup.string()
        .required('IČO je povinné.')
        .matches(/^\d+$/, 'IČO musí obsahovať len číslice.')
        .min(6, 'Minimálny počet znakov je 6.')
        .max(8, 'Maximálny počet znakov je 8.'),
    vatId: Yup.string()
        .required('DIČ je povinné.')
        .matches(/^\d{10}$/, 'DIČ musí mať presne 10 číslic.'),
    vatRegNo: Yup.string()
        .matches(/^SK\d{10}$/, 'DIČ DPH musí byť vo formáte SK + 10 číslic.')
        .max(12, 'Maximálny počet znakov je 12.'),
    type: Yup.string().required('Typ účtu je povinný.'),
    responsiblePerson: Yup.string()
        .required('Zodpovedná osoba je povinná.')
        .min(3, 'Minimálny počet znakov je 3.')
        .max(150, 'Maximálny počet znakov je 150.'),
});

const STEP_FIELDS = {
    1: ['companyName', 'address.street', 'address.registrationNumber', 'address.buildingNumber', 'address.zip', 'address.city', 'address.country', 'identificationNumber', 'vatId', 'vatRegNo'],
    2: ['type', 'responsiblePerson'],
};

const getNestedError = (errors, path) =>
    path.split('.').reduce((obj, key) => obj?.[key], errors);

const buildNestedTouched = (fields) =>
    fields.reduce((acc, path) => {
        const keys = path.split('.');
        let cur = acc;
        keys.forEach((key, i) => {
            if (i === keys.length - 1) cur[key] = true;
            else { cur[key] = cur[key] || {}; cur = cur[key]; }
        });
        return acc;
    }, {});

const STEPS = [
    { label: 'Údaje o spoločnosti' },
    { label: 'Záverečné údaje' },
    { label: 'Výber plánu' },
];

const FEATURE_LABELS = {
    maxBranches:      ['Prevádzka',      'Prevádzky',        'Prevádzok'],
    maxEvidenceLists: ['Evidenčný list', 'Evidenčné listy',  'Evidenčných listov'],
    maxClients:       ['Klient',         'Klienti',          'Klientov'],
    maxWarehouses:    ['Sklad',          'Sklady',           'Skladov'],
    maxContainers:    ['Nádoba',         'Nádoby',           'Nádob'],
    maxConsents:      ['Súhlas',         'Súhlasy',          'Súhlasov'],
};
const formatValue = (v) => (v >= 9999 ? 'Neobmedzene' : v);
const pluralize = (val, forms) => {
    if (val >= 9999) return forms[2];
    if (val === 1) return forms[0];
    if (val >= 2 && val <= 4) return forms[1];
    return forms[2];
};
const formatEur = (cents) => `${(cents / 100).toFixed(2).replace('.', ',')} €`;

const cardElementOptions = {
    style: {
        base: { fontSize: '15px', color: '#0B1157', fontFamily: '"Open Sans", sans-serif', '::placeholder': { color: '#718094' } },
        invalid: { color: '#dc3545' },
    },
};

// ── Payment sub-form (needs to be inside <Elements>) ──────────────────────────
const PaymentForm = ({ selectedPlan, module = 'waste', tenantId, onSuccess, onError, onBack }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const planData = (module === 'ppwr' ? PPWR_PLAN_LIMITS : PLAN_LIMITS)[selectedPlan];

    const handlePay = async () => {
        if (!stripe || !elements) return;
        setLoading(true);
        setError(null);

        try {
            const { data } = await axios.post(
                '/api/payment/create-payment-intent',
                { plan: selectedPlan, module },
                getAuthHeader()
            );

            const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
                data.clientSecret,
                { payment_method: { card: elements.getElement(CardElement) } }
            );

            if (stripeError) { setError(stripeError.message); return; }

            if (paymentIntent.status === 'succeeded') {
                await axios.post('/api/payment/activate', { paymentIntentId: paymentIntent.id, module }, getAuthHeader());
                onSuccess();
            }
        } catch (err) {
            const msg = err.response?.data?.message ?? 'Nastala chyba. Skúste znova.';
            setError(msg);
            onError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mt-3">
            <div
                className="p-3 rounded mb-3"
                style={{ border: '1px solid #EDF2F7', backgroundColor: '#fff' }}
            >
                <CardElement options={cardElementOptions} />
            </div>

            {error && (
                <Alert variant="danger" style={{ fontSize: '14px' }} className="mb-3">
                    {error}
                </Alert>
            )}

            <div className="d-flex gap-2 justify-content-end">
                <Button
                    variant="link"
                    onClick={onBack}
                    disabled={loading}
                    style={{ color: '#718094', textDecoration: 'none' }}
                >
                    Späť
                </Button>
                <Button
                    className="btn-custom-small px-4"
                    onClick={handlePay}
                    disabled={!stripe || loading}
                >
                    {loading ? (
                        <><Spinner animation="border" size="sm" className="me-2" />Spracúvam...</>
                    ) : (
                        `Zaplatiť ${formatEur(priceWithVat(planData?.price))}`
                    )}
                </Button>
            </div>
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────────
const TenantSetup = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const users = useSelector(state => state.users);
    const currentCompany = useSelector(state => state.companySearch?.current);

    const [step, setStep] = useState(1);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedPpwrPlan, setSelectedPpwrPlan] = useState(null);
    const [ppwrEnabled, setPpwrEnabled] = useState(false);
    const [paymentPending, setPaymentPending] = useState(false);
    const [ppwrPaymentPending, setPpwrPaymentPending] = useState(false);
    const [paymentError, setPaymentError] = useState(null);

    const show = users.auth === true && (
        !users.data?.tenant ||
        users.data.tenant.length === 0 ||
        paymentPending ||
        ppwrPaymentPending
    );

    const handleSignOut = async () => {
        await dispatch(signOut());
        navigate('/account/login');
    };

    const formik = useFormik({
        initialValues: {
            companyName: '',
            address: { street: '', registrationNumber: '', buildingNumber: '', zip: '', city: '', country: 'Slovensko' },
            identificationNumber: '',
            vatId: '',
            vatRegNo: '',
            type: '',
            responsiblePerson: '',
        },
        validationSchema: validation,
        onSubmit: () => {},
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!currentCompany?.companyName) return;
        let street = '', buildingNumber = '', registrationNumber = '';
        if (currentCompany.street) {
            const match = currentCompany.street.match(/(.*?)(\d+[a-zA-Z]?(?:\/\d+[a-zA-Z]?)?)$/);
            if (match) {
                street = match[1].trim();
                const numberPart = match[2].trim();
                const slashIndex = numberPart.indexOf('/');
                if (slashIndex !== -1) {
                    buildingNumber = numberPart.slice(0, slashIndex);
                    registrationNumber = numberPart.slice(slashIndex + 1);
                } else {
                    buildingNumber = numberPart;
                }
            }
        }
        formik.setFieldValue('companyName', currentCompany.companyName || '');
        formik.setFieldValue('address.street', street);
        formik.setFieldValue('address.registrationNumber', registrationNumber);
        formik.setFieldValue('address.buildingNumber', buildingNumber);
        formik.setFieldValue('address.zip', currentCompany.zip || '');
        formik.setFieldValue('address.city', currentCompany.city || '');
        formik.setFieldValue('address.country', currentCompany.country || 'Slovensko');
        formik.setFieldValue('identificationNumber', currentCompany.identificationNumber || '');
        formik.setFieldValue('vatId', currentCompany.vatId || '');
        formik.setFieldValue('vatRegNo', currentCompany.vatRegNo || '');
    }, [currentCompany]);

    const handleNext = async () => {
        const allErrors = await formik.validateForm();
        const hasErrors = STEP_FIELDS[step].some(field => getNestedError(allErrors, field));
        if (hasErrors) {
            formik.setTouched(buildNestedTouched(STEP_FIELDS[step]));
        } else {
            setStep(s => s + 1);
        }
    };

    const validateAll = async () => {
        const errors = await formik.validateForm();
        const allFields = [...STEP_FIELDS[1], ...STEP_FIELDS[2]];
        const hasErrors = allFields.some(field => getNestedError(errors, field));
        if (hasErrors) {
            formik.setTouched(buildNestedTouched(allFields));
            setStep(1);
            return false;
        }
        return true;
    };

    const activatePpwrDemo = async () => {
        try {
            await axios.post('/api/tenant/activate-module', { module: 'ppwr' }, getAuthHeader());
        } catch (_) {}
    };

    const handleConfirmPlans = async () => {
        const hasWastePlan = !!selectedPlan;
        const ppwrSelected = ppwrEnabled && !!selectedPpwrPlan;

        if (!hasWastePlan && !ppwrSelected) return;
        if (!await validateAll()) return;

        if (!hasWastePlan && ppwrSelected) {
            const action = await dispatch(setupTenantProfile({ ...formik.values, includeWaste: false }));
            if (!setupTenantProfile.fulfilled.match(action)) return;
            if (selectedPpwrPlan === 'demo') {
                await activatePpwrDemo();
            } else {
                setPpwrPaymentPending(true);
            }
            return;
        }

        if (selectedPlan === FREE_PLAN) {
            const action = await dispatch(setupTenantProfile(formik.values));
            if (!setupTenantProfile.fulfilled.match(action)) return;
            if (ppwrSelected) {
                if (selectedPpwrPlan === 'demo') {
                    await activatePpwrDemo();
                } else {
                    setPpwrPaymentPending(true);
                }
            }
        } else {
            setPaymentPending(true);
            setPaymentError(null);
            const action = await dispatch(setupTenantProfile(formik.values));
            if (!setupTenantProfile.fulfilled.match(action)) {
                setPaymentPending(false);
            }
        }
    };

    const handlePaymentSuccess = async () => {
        setPaymentPending(false);
        const ppwrSelected = ppwrEnabled && !!selectedPpwrPlan;
        if (ppwrSelected) {
            if (selectedPpwrPlan === 'demo') {
                await activatePpwrDemo();
            } else {
                setPpwrPaymentPending(true);
            }
        }
    };

    const handlePpwrPaymentSuccess = () => {
        setPpwrPaymentPending(false);
    };

    const handlePaymentError = (msg) => {
        setPaymentError(msg);
    };

    const handleBackFromPayment = () => {
        setPaymentPending(false);
        setSelectedPlan(null);
    };

    if (!show) return null;

    const userName = users.data?.name ? `, ${users.data.name}` : '';
    const tenantId = users.data?.createdTenantId;

    return (
        <div className="onboarding-overlay">

            {/* Left column */}
            <div className="onboarding-left">
                <div className="onboarding-left-content">
                    <h2>Len pár krokov vás delí od evidencie odpadov.</h2>
                    <p>Nastavte profil vašej organizácie a začnite ihneď pracovať.</p>
                    <div className="onboarding-steps-preview">
                        {STEPS.map((s, i) => {
                            const num = i + 1;
                            const isDone = step > num;
                            const isActive = step === num;
                            return (
                                <div key={num} className={`onboarding-step-preview ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}>
                                    <div className="step-preview-dot">{isDone ? '✓' : num}</div>
                                    <span className="step-preview-label">{s.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Right form column */}
            <div className="onboarding-right">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <Image src={logo} style={{ height: '38px', width: 'auto' }} />
                    <Button className="btn-custom-small" onClick={handleSignOut}>Odhlásiť sa</Button>
                </div>

                <div className="onboarding-heading">
                    <h1>Vitajte{userName}!</h1>
                    <p>Vyplňte údaje o vašej organizácii. Trvá to len minútu.</p>
                    <div className="onboarding-progress mt-3">
                        {STEPS.map((_, i) => (
                            <div key={i} className={`onboarding-progress-bar ${step > i ? 'filled' : ''}`} />
                        ))}
                    </div>
                </div>

                <Form onSubmit={e => e.preventDefault()}>

                    {/* ── STEP 1 ── */}
                    {step === 1 && (
                        <>
                            <div className="onboarding-section">
                                <div className="onboarding-section-title">Základné údaje</div>
                                <CompanySearch formik={formik} />
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>IČO*</Form.Label>
                                            <Form.Control type="number" className="radius no-spinner" placeholder="Zadajte IČO:"
                                                {...formik.getFieldProps('identificationNumber')}
                                                isInvalid={errorHelper(formik, 'identificationNumber').error} />
                                            <Form.Control.Feedback type="invalid">{errorHelper(formik, 'identificationNumber').helperText}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>DIČ*</Form.Label>
                                            <Form.Control type="text" className="radius" placeholder="Zadajte DIČ:"
                                                {...formik.getFieldProps('vatId')}
                                                isInvalid={errorHelper(formik, 'vatId').error} />
                                            <Form.Control.Feedback type="invalid">{errorHelper(formik, 'vatId').helperText}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-0">
                                    <Form.Label>DIČ DPH <span className="text-muted">(nepovinné)</span></Form.Label>
                                    <Form.Control type="text" className="radius" placeholder="SK + 10 číslic, napr. SK1234567890"
                                        {...formik.getFieldProps('vatRegNo')}
                                        isInvalid={errorHelper(formik, 'vatRegNo').error} />
                                    <Form.Control.Feedback type="invalid">{errorHelper(formik, 'vatRegNo').helperText}</Form.Control.Feedback>
                                </Form.Group>
                            </div>

                            <div className="onboarding-section">
                                <div className="onboarding-section-title">Adresa sídla</div>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Ulica*</Form.Label>
                                            <Form.Control type="text" className="radius" placeholder="Zadajte ulicu:"
                                                {...formik.getFieldProps('address.street')}
                                                isInvalid={formik.touched.address?.street && !!formik.errors.address?.street} />
                                            <Form.Control.Feedback type="invalid">{formik.errors.address?.street}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Súpisné č.</Form.Label>
                                            <Form.Control type="text" className="radius" placeholder="Napr. 123"
                                                {...formik.getFieldProps('address.registrationNumber')}
                                                isInvalid={formik.touched.address?.registrationNumber && !!formik.errors.address?.registrationNumber} />
                                            <Form.Control.Feedback type="invalid">{formik.errors.address?.registrationNumber}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Orient. č.</Form.Label>
                                            <Form.Control type="text" className="radius" placeholder="Napr. 4A"
                                                {...formik.getFieldProps('address.buildingNumber')}
                                                isInvalid={formik.touched.address?.buildingNumber && !!formik.errors.address?.buildingNumber} />
                                            <Form.Control.Feedback type="invalid">{formik.errors.address?.buildingNumber}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>PSČ*</Form.Label>
                                            <Form.Control type="text" className="radius" placeholder="Napr. 01001"
                                                {...formik.getFieldProps('address.zip')}
                                                isInvalid={formik.touched.address?.zip && !!formik.errors.address?.zip} />
                                            <Form.Control.Feedback type="invalid">{formik.errors.address?.zip}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Mesto*</Form.Label>
                                            <Form.Control type="text" className="radius" placeholder="Zadajte mesto:"
                                                {...formik.getFieldProps('address.city')}
                                                isInvalid={formik.touched.address?.city && !!formik.errors.address?.city} />
                                            <Form.Control.Feedback type="invalid">{formik.errors.address?.city}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Krajina</Form.Label>
                                            <Form.Control type="text" className="radius" {...formik.getFieldProps('address.country')} readOnly />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        </>
                    )}

                    {/* ── STEP 2 ── */}
                    {step === 2 && (
                        <>
                            <div className="onboarding-section">
                                <div className="onboarding-section-title">Typ organizácie</div>
                                <Form.Group className="mb-0">
                                    <Form.Label>Typ účtu*</Form.Label>
                                    <Form.Select className="radius" {...formik.getFieldProps('type')} isInvalid={errorHelper(formik, 'type').error}>
                                        <option value="">Vyberte typ účtu:</option>
                                        <option value="company">Firma</option>
                                        <option value="school">Škola</option>
                                        <option value="town">Obec</option>
                                        <option value="transporter">Prepravca</option>
                                        <option value="recycling center">Recyklačné centrum</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{errorHelper(formik, 'type').helperText}</Form.Control.Feedback>
                                </Form.Group>
                            </div>

                            <div className="onboarding-section">
                                <div className="onboarding-section-title">Zodpovedná osoba</div>
                                <Form.Group className="mb-0">
                                    <Form.Label>Meno a priezvisko zodpovednej osoby*</Form.Label>
                                    <Form.Control type="text" className="radius" placeholder="Napr. Ján Novák"
                                        {...formik.getFieldProps('responsiblePerson')}
                                        isInvalid={errorHelper(formik, 'responsiblePerson').error} />
                                    <Form.Control.Feedback type="invalid">{errorHelper(formik, 'responsiblePerson').helperText}</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                        </>
                    )}

                    {/* ── STEP 3 — Plan selection ── */}
                    {step === 3 && !paymentPending && !ppwrPaymentPending && (
                        <div className="onboarding-section">
                            {/* Evidencia odpadov */}
                            <div className="onboarding-section-title">Evidencia odpadov <span style={{ fontSize: '11px', fontWeight: 400, color: '#718094', textTransform: 'none', letterSpacing: 0 }}>(nepovinné)</span></div>
                            <p style={{ color: '#718094', fontSize: '13px', marginBottom: '16px' }}>
                                Demo plán je zadarmo na 30 dní. Platené plány aktivujete okamžite. Môžete preskočiť, ak vás zaujíma iba PPWR.
                            </p>
                            <Row className="g-3 mb-4">
                                {Object.entries(PLAN_LIMITS).map(([planKey, planData]) => (
                                    <Col key={planKey} xs={12} md={4}>
                                        <div
                                            onClick={() => setSelectedPlan(p => p === planKey ? null : planKey)}
                                            className="p-3 rounded h-100 d-flex flex-column"
                                            style={{
                                                cursor: 'pointer',
                                                border: selectedPlan === planKey ? '2px solid #0B1157' : '1px solid #EDF2F7',
                                                backgroundColor: '#fff',
                                                position: 'relative',
                                                transition: 'border-color 0.15s',
                                            }}
                                        >
                                            {planKey === 'start' && (
                                                <span style={{
                                                    position: 'absolute', top: '-11px', left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    backgroundColor: '#0B1157', color: '#4DFFFF',
                                                    fontSize: '10px', fontWeight: 700,
                                                    padding: '2px 12px', borderRadius: '20px',
                                                    whiteSpace: 'nowrap',
                                                }}>ODPORÚČANÉ</span>
                                            )}
                                            <p style={{ color: '#718094', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 2px' }}>Plán</p>
                                            <h6 style={{ color: '#0B1157', fontWeight: 700, margin: '0 0 8px' }}>{planData.label}</h6>
                                            <div className="mb-3">
                                                {planData.price === 0 ? (
                                                    <>
                                                        <span style={{ color: '#0B1157', fontSize: '20px', fontWeight: 700 }}>Zadarmo</span>
                                                        <div style={{ color: '#718094', fontSize: '11px', marginTop: '2px' }}>30 dní zadarmo</div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span style={{ color: '#0B1157', fontSize: '20px', fontWeight: 700 }}>{formatEur(planData.price)}</span>
                                                        <span style={{ color: '#718094', fontSize: '12px' }}> bez DPH / rok</span>
                                                        <div style={{ color: '#718094', fontSize: '11px', marginTop: '2px' }}>
                                                            {formatEur(priceWithVat(planData.price))} s DPH ({Math.round(VAT_RATE * 100)} %)
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            <ul className="list-unstyled mb-0 flex-grow-1" style={{ fontSize: '13px' }}>
                                                {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                                                    <li key={key} className="d-flex align-items-center mb-1" style={{ color: '#4A5568' }}>
                                                        <BsCheckLg style={{ color: '#0B1157', marginRight: '8px', flexShrink: 0 }} />
                                                        <span><strong>{formatValue(planData[key])}</strong> {pluralize(planData[key], label)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            {/* PPWR */}
                            <div style={{
                                marginTop: '24px',
                                backgroundColor: ppwrEnabled ? '#F4F5FE' : '#FAFBFF',
                                border: ppwrEnabled ? '2px solid #0B1157' : '1px solid #D8DCEF',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                transition: 'border-color 0.2s, background-color 0.2s',
                            }}>
                                {/* Toggle header — klikateľná celá plocha */}
                                <div
                                    onClick={() => {
                                        const next = !ppwrEnabled;
                                        setPpwrEnabled(next);
                                        if (next) setSelectedPpwrPlan('demo');
                                        else setSelectedPpwrPlan(null);
                                    }}
                                    style={{ padding: '18px 20px', cursor: 'pointer', userSelect: 'none' }}
                                >
                                    <div className="d-flex align-items-center justify-content-between gap-3">
                                        <div className="d-flex align-items-start gap-3">
                                            <div style={{
                                                width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
                                                backgroundColor: ppwrEnabled ? '#0B1157' : '#E8EAF2',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                transition: 'background-color 0.2s',
                                            }}>
                                                <IoCubeOutline size={20} style={{ color: ppwrEnabled ? '#4DFFFF' : '#718094' }} />
                                            </div>
                                            <div>
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <span style={{ fontWeight: 700, color: '#0B1157', fontSize: '15px' }}>
                                                        Mám záujem aj o PPWR modul
                                                    </span>
                                                    <span style={{
                                                        backgroundColor: '#0B1157', color: '#4DFFFF',
                                                        fontSize: '10px', fontWeight: 700,
                                                        padding: '2px 8px', borderRadius: '20px',
                                                        letterSpacing: '0.5px', flexShrink: 0,
                                                    }}>PPWR</span>
                                                </div>
                                                <p style={{ color: '#718094', fontSize: '13px', margin: 0, lineHeight: 1.5 }}>
                                                    Obalové hospodárstvo — správa obalových jednotiek podľa nariadenia PPWR
                                                </p>
                                            </div>
                                        </div>
                                        <Form.Check
                                            type="switch"
                                            id="ppwr-toggle"
                                            checked={ppwrEnabled}
                                            onChange={e => {
                                                const next = e.target.checked;
                                                setPpwrEnabled(next);
                                                if (next) setSelectedPpwrPlan('demo');
                                                else setSelectedPpwrPlan(null);
                                            }}
                                            onClick={e => e.stopPropagation()}
                                            style={{ fontSize: '18px', flexShrink: 0 }}
                                        />
                                    </div>
                                </div>

                                {/* Plány — viditeľné len keď je toggle ON */}
                                {ppwrEnabled && (
                                    <div style={{ borderTop: '1px solid #D8DCEF', padding: '20px' }}>
                                        <Row className="g-3">
                                            {Object.entries(PPWR_PLAN_LIMITS).map(([planKey, planData]) => (
                                                <Col key={planKey} xs={12} sm={6} md={3}>
                                                    <div
                                                        onClick={() => {
                                                        if (selectedPpwrPlan === planKey) {
                                                            setSelectedPpwrPlan(null);
                                                            setPpwrEnabled(false);
                                                        } else {
                                                            setSelectedPpwrPlan(planKey);
                                                        }
                                                    }}
                                                        className="p-3 rounded h-100 d-flex flex-column"
                                                        style={{
                                                            cursor: 'pointer',
                                                            border: selectedPpwrPlan === planKey ? '2px solid #0B1157' : '1px solid #D8DCEF',
                                                            backgroundColor: '#fff',
                                                            position: 'relative',
                                                            transition: 'border-color 0.15s',
                                                        }}
                                                    >
                                                        {planKey === 'M' && (
                                                            <span style={{
                                                                position: 'absolute', top: '-11px', left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                backgroundColor: '#0B1157', color: '#4DFFFF',
                                                                fontSize: '10px', fontWeight: 700,
                                                                padding: '2px 12px', borderRadius: '20px',
                                                                whiteSpace: 'nowrap',
                                                            }}>ODPORÚČANÉ</span>
                                                        )}
                                                        <p style={{ color: '#718094', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 2px' }}>PPWR plán</p>
                                                        <h6 style={{ color: '#0B1157', fontWeight: 700, margin: '0 0 4px' }}>{planData.label}</h6>
                                                        {planData.tagline && (
                                                            <p style={{ color: '#718094', fontSize: '11px', margin: '0 0 8px', lineHeight: '1.4' }}>{planData.tagline}</p>
                                                        )}
                                                        <div className="mb-3">
                                                            {planData.price === 0 ? (
                                                                <>
                                                                    <span style={{ color: '#0B1157', fontSize: '18px', fontWeight: 700 }}>Zadarmo</span>
                                                                    <div style={{ color: '#718094', fontSize: '11px' }}>30 dní zadarmo</div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span style={{ color: '#0B1157', fontSize: '18px', fontWeight: 700 }}>{formatEur(planData.price)}</span>
                                                                    <span style={{ color: '#718094', fontSize: '11px' }}> bez DPH/rok</span>
                                                                </>
                                                            )}
                                                        </div>
                                                        <ul className="list-unstyled mb-0 flex-grow-1" style={{ fontSize: '12px' }}>
                                                            <li className="d-flex align-items-start mb-1" style={{ color: '#4A5568' }}>
                                                                <BsCheckLg style={{ color: '#0B1157', marginRight: '6px', flexShrink: 0, marginTop: '2px' }} />
                                                                <span><strong>{formatValue(planData.maxPackagingUnits)}</strong> {pluralize(planData.maxPackagingUnits, ['obalová jednotka', 'obalové jednotky', 'obalových jednotiek'])}</span>
                                                            </li>
                                                            {planData.features?.map((feature, i) => (
                                                                <li key={i} className="d-flex align-items-start mb-1" style={{ color: '#4A5568' }}>
                                                                    <BsCheckLg style={{ color: '#0B1157', marginRight: '6px', flexShrink: 0, marginTop: '2px' }} />
                                                                    <span>{feature}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── STEP 3 — Waste payment form ── */}
                    {step === 3 && paymentPending && tenantId && (
                        <div className="onboarding-section">
                            <div className="onboarding-section-title">
                                Platba — plán {PLAN_LIMITS[selectedPlan]?.label}
                            </div>
                            <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#F4F5FE' }}>
                                <div className="d-flex justify-content-between">
                                    <span style={{ color: '#718094', fontSize: '14px' }}>Ročný poplatok bez DPH</span>
                                    <span style={{ color: '#718094', fontSize: '14px' }}>{formatEur(PLAN_LIMITS[selectedPlan]?.price)}</span>
                                </div>
                                <div className="d-flex justify-content-between mt-1">
                                    <span style={{ color: '#718094', fontSize: '14px' }}>DPH ({Math.round(VAT_RATE * 100)} %)</span>
                                    <span style={{ color: '#718094', fontSize: '14px' }}>{formatEur(priceWithVat(PLAN_LIMITS[selectedPlan]?.price) - PLAN_LIMITS[selectedPlan]?.price)}</span>
                                </div>
                                <hr style={{ margin: '8px 0', borderColor: '#EDF2F7' }} />
                                <div className="d-flex justify-content-between">
                                    <span style={{ color: '#0B1157', fontWeight: 600 }}>Celkom</span>
                                    <span style={{ color: '#0B1157', fontWeight: 700 }}>{formatEur(priceWithVat(PLAN_LIMITS[selectedPlan]?.price))}</span>
                                </div>
                            </div>
                            <Elements stripe={stripePromise}>
                                <PaymentForm
                                    selectedPlan={selectedPlan}
                                    module="waste"
                                    tenantId={tenantId}
                                    onSuccess={handlePaymentSuccess}
                                    onError={handlePaymentError}
                                    onBack={handleBackFromPayment}
                                />
                            </Elements>
                            <p style={{ fontSize: '12px', color: '#a0aec0', marginTop: '12px', textAlign: 'center' }}>
                                Konto bolo vytvorené. Ak platba zlyhá, môžete zaplatiť neskôr v nastaveniach.
                            </p>
                        </div>
                    )}

                    {/* ── STEP 3 — PPWR payment form ── */}
                    {step === 3 && ppwrPaymentPending && tenantId && (
                        <div className="onboarding-section">
                            <div className="onboarding-section-title">
                                Platba — PPWR plán {PPWR_PLAN_LIMITS[selectedPpwrPlan]?.label}
                            </div>
                            <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#F4F5FE' }}>
                                <div className="d-flex justify-content-between">
                                    <span style={{ color: '#718094', fontSize: '14px' }}>Ročný poplatok bez DPH</span>
                                    <span style={{ color: '#718094', fontSize: '14px' }}>{formatEur(PPWR_PLAN_LIMITS[selectedPpwrPlan]?.price)}</span>
                                </div>
                                <div className="d-flex justify-content-between mt-1">
                                    <span style={{ color: '#718094', fontSize: '14px' }}>DPH ({Math.round(VAT_RATE * 100)} %)</span>
                                    <span style={{ color: '#718094', fontSize: '14px' }}>{formatEur(priceWithVat(PPWR_PLAN_LIMITS[selectedPpwrPlan]?.price) - PPWR_PLAN_LIMITS[selectedPpwrPlan]?.price)}</span>
                                </div>
                                <hr style={{ margin: '8px 0', borderColor: '#EDF2F7' }} />
                                <div className="d-flex justify-content-between">
                                    <span style={{ color: '#0B1157', fontWeight: 600 }}>Celkom</span>
                                    <span style={{ color: '#0B1157', fontWeight: 700 }}>{formatEur(priceWithVat(PPWR_PLAN_LIMITS[selectedPpwrPlan]?.price))}</span>
                                </div>
                            </div>
                            <Elements stripe={stripePromise}>
                                <PaymentForm
                                    selectedPlan={selectedPpwrPlan}
                                    module="ppwr"
                                    tenantId={tenantId}
                                    onSuccess={handlePpwrPaymentSuccess}
                                    onError={handlePaymentError}
                                    onBack={() => setPpwrPaymentPending(false)}
                                />
                            </Elements>
                            <p style={{ fontSize: '12px', color: '#a0aec0', marginTop: '12px', textAlign: 'center' }}>
                                Môžete zaplatiť neskôr v nastaveniach predplatného.
                            </p>
                        </div>
                    )}

                    {/* Footer navigation */}
                    {!paymentPending && !ppwrPaymentPending && (
                        <div className="onboarding-footer">
                            <span className="onboarding-step-counter">Krok {step} z {STEPS.length}</span>
                            <div className="onboarding-actions">
                                {step > 1 && (
                                    <Button className="btn-onboarding-back" onClick={() => setStep(s => s - 1)}>
                                        Späť
                                    </Button>
                                )}
                                {step < 3 && (
                                    <Button className="btn-onboarding-next" onClick={handleNext}>
                                        Pokračovať →
                                    </Button>
                                )}
                                {step === 3 && (
                                    <Button
                                        className="btn-onboarding-next"
                                        onClick={handleConfirmPlans}
                                        disabled={(!selectedPlan && !(ppwrEnabled && selectedPpwrPlan)) || users.loading}
                                    >
                                        {users.loading ? (
                                            <><Spinner animation="border" size="sm" className="me-1" /> Spracúvam...</>
                                        ) : (
                                            'Potvrdiť výber →'
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                </Form>
            </div>
        </div>
    );
};

export default TenantSetup;
